import { CustomerOrderPickableLinesDataGridApi } from "../../../../../api/generated/erp/sales/picking/api/customerOrderPickableLinesDataGridApi.ts";
import { CustomerOrderLinePickingObjectLocationView } from "../../../../../api/generated/erp/db/types/tables/customerOrderLinePickingObjectLocationView.ts";
import { useState } from "react";
import { ControlledAsyncCrudDataGrid } from "../../../../common/dataGrid/crud/ControlledAsyncCrudDataGrid.tsx";
import { useAsyncFetch } from "../../../../../utils/async/asyncFetch.ts";
import { floatColumn, integerColumn, textColumn } from "../../../../common/dataGrid/columns.tsx";
import i18n from "i18next";
import { dataGridPersistentStateProps } from "../../../../common/dataGrid/dataGridStateStorage.ts";
import { AsyncButton } from "../../../../common/buttons/AsyncButton.tsx";
import { faBoxOpen, faPrint, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { downloadFile } from "../../../../../utils/fileDownloading.ts";
import { CustomerOrderPackageView } from "../../../../../api/generated/erp/db/types/tables/customerOrderPackageView.ts";
import { RefreshableElementProps, setRefreshRefValue } from "../../../../../utils/useRefreshRef.ts";
import { useInputDialog } from "../../../../common/dialogs/input/useInputDialog.tsx";

export interface CustomerOrderPickableLinesDataGridProps extends RefreshableElementProps {
	customerOrderId: number;
	customerOrderPackage: CustomerOrderPackageView | undefined;
	packageLinesDataGridRefreshRef?: () => void;
	reservableCustomerOrderLinesDataGridRefreshRef?: () => void;
	customerOrderAutomaticReservation: boolean;
}

export const CustomerOrderPickableLinesDataGrid = ({
	customerOrderId,
	customerOrderPackage,
	refreshRef,
	packageLinesDataGridRefreshRef,
	reservableCustomerOrderLinesDataGridRefreshRef,
	customerOrderAutomaticReservation,
}: CustomerOrderPickableLinesDataGridProps) => {
	const [selectedRow, setSelectedRow] = useState<
		CustomerOrderLinePickingObjectLocationView | undefined
	>(undefined);
	const [rowsAsync, refresh] = useAsyncFetch(() =>
		CustomerOrderPickableLinesDataGridApi.getLines({ customerOrderId: customerOrderId }),
	);
	setRefreshRefValue(refreshRef, refresh);

	const showInputDialog = useInputDialog();

	return (
		<ControlledAsyncCrudDataGrid<CustomerOrderLinePickingObjectLocationView>
			rowsAsync={rowsAsync}
			refreshData={refresh}
			getRowId={(row) => row.warehousePickingObjectLocationId}
			onRowSelectionChanged={(rows) => {
				setSelectedRow(rows[0]);
			}}
			columns={[
				integerColumn({
					field: "lineNumber",
					headerName: i18n.t("line_number"),
				}),
				textColumn({
					field: "partLongName",
					headerName: i18n.t("part"),
					width: 300,
				}),
				floatColumn({
					field: "locationReservedQuantity",
					headerName: i18n.t("pickable_quantity"),
					width: 150,
				}),
				textColumn({
					field: "partUnit",
					headerName: i18n.t("unit"),
				}),
				textColumn({
					field: "locationCode",
					headerName: i18n.t("warehouse_location"),
				}),
			]}
			{...dataGridPersistentStateProps("AC8A87916766FBE8")}
			actionBarComponents={
				<>
					<AsyncButton
						label={i18n.t("picking_list")}
						variant={"outlined"}
						icon={faPrint}
						onClick={async () => {
							const fileHandle =
								await CustomerOrderPickableLinesDataGridApi.printCustomerOrderPickingList(
									{
										customerOrderId: customerOrderId,
									},
								);
							downloadFile(fileHandle);
						}}
					/>
					<AsyncButton
						label={i18n.t("pick")}
						variant={"outlined"}
						icon={faBoxOpen}
						disabled={() => {
							if (!selectedRow) return i18n.t("select_line");
							if (customerOrderPackage === undefined) return i18n.t("select_package");
							if (customerOrderPackage.customerOrderPackageState === "PICKED")
								return i18n.t("package_is_picked");
							return false;
						}}
						onClick={async () => {
							if (!selectedRow) {
								return;
							}
							const pickQuantity = await showInputDialog({
								title: i18n.t("quantity"),
								type: "decimal",
								defaultValue: selectedRow.locationReservedQuantity,
								required: true,
							});
							if (pickQuantity === undefined) return;
							if (customerOrderPackage === undefined) return;
							await CustomerOrderPickableLinesDataGridApi.pickCustomerOrderLineToPackage(
								{
									customerOrderLineId: selectedRow.customerOrderLineId,
									customerOrderPackageId:
										customerOrderPackage.customerOrderPackageId,
									quantityInWarehouseUnit: pickQuantity,
									partWarehouseLocationId: selectedRow.partWarehouseLocationId,
								},
							);
							packageLinesDataGridRefreshRef?.();
							await refresh();
						}}
					/>
					{!customerOrderAutomaticReservation && (
						<AsyncButton
							label={i18n.t("revert_reservation")}
							variant={"outlined"}
							icon={faUndo}
							disabled={() => {
								if (!selectedRow) return i18n.t("select_line");
								return false;
							}}
							onClick={async () => {
								if (!selectedRow) return;
								const revertQuantity = await showInputDialog({
									title: i18n.t("quantity_to_cancel"),
									type: "decimal",
									defaultValue: selectedRow.locationReservedQuantity,
									required: true,
								});
								if (revertQuantity === undefined) return;
								await CustomerOrderPickableLinesDataGridApi.revertCustomerOrderLineReservation(
									{
										customerOrderLinePickingObjectLocation: selectedRow,
										quantityToRevertInWarehouseUnit: revertQuantity,
									},
								);
								reservableCustomerOrderLinesDataGridRefreshRef?.();
								await refresh();
							}}
						/>
					)}
				</>
			}
		/>
	);
};
